import React, { useMemo, useState } from 'react'
import cx from 'classnames'
import { useDispatch, useSelector } from 'react-redux'
import { Button, NoContent, Table, TextField, TextInput } from '../../../../views/components'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import { apiStateSelectors } from '../../../api/apiState'
import { agencyApiSelectors } from '../../../api/agency'
import { userApiEvents, userApiSelectors } from '../../../api/user'
import { TAdminUserLookup, TBulkAddOutcome } from '../../../api/user/types'
import RemoveMembershipModal, { TRemoveTarget } from '../RemoveMembershipModal/RemoveMembershipModal'
import AccountStatusModal, { TAccountStatusChange } from '../AccountStatusModal/AccountStatusModal'
import TestAgencyPill, { useTestAgencyIds } from '../TestAgencyPill/TestAgencyPill'
import { agencyDisplayName, agencyEntityLine, roleLabel } from '../../labels'
import styles from './UserAccess.module.scss'

type TProps = {
  onGrantAccess: (user: TAdminUserLookup) => void
}

const STATUS_LABEL: Record<TBulkAddOutcome['status'], string> = {
  Added: 'Added',
  AlreadyMember: 'Already a member',
  Failed: 'Failed',
}

const UserAccess = ({ onGrantAccess }: TProps) => {
  const dispatch = useDispatch()
  const [email, setEmail] = useState('')
  const [removeTarget, setRemoveTarget] = useState<TRemoveTarget | null>(null)
  const [statusTarget, setStatusTarget] = useState<{ email: string; enable: boolean } | null>(null)

  const lookup = useSelector(userApiSelectors.getAdminUserLookup)
  const emailMatches = useSelector(userApiSelectors.getAdminEmailMatches)
  const testAgencyIds = useTestAgencyIds()
  const grantResults = useSelector(userApiSelectors.getAdminGrantResults)
  const agencyListing = useSelector(agencyApiSelectors.getAgencyListing)
  const isLookingUp = useSelector(state => apiStateSelectors.isLoading(state)([userApiEvents.adminLookupUser_request]))
  const isChangingStatus = useSelector(state =>
    apiStateSelectors.isLoading(state)([userApiEvents.enableUser_request, userApiEvents.disableUser_request]),
  )

  // Grant outcomes only carry the id.
  const agencyNames = useMemo(
    () => new Map((agencyListing ?? []).map(a => [a.agencyId, agencyDisplayName(a)])),
    [agencyListing],
  )

  const lookUp = (address: string) => {
    dispatch(userApiEvents.adminSearchEmails_clear())
    dispatch(userApiEvents.adminLookupUser_request({ email: address }))
  }

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    if (email.trim()) lookUp(email)
  }

  const onEmailChange = (value: string) => {
    setEmail(value)
    dispatch(userApiEvents.adminSearchEmails_request({ q: value }))
  }

  const pickMatch = (address: string) => {
    setEmail(address)
    lookUp(address)
  }

  // Hidden once the typed text is itself a match, so a full address does not
  // leave a one-item list hanging under the field.
  const suggestions = emailMatches.filter(m => m.email !== email.trim().toLowerCase())

  const confirmRemove = (target: TRemoveTarget) => {
    dispatch(
      userApiEvents.adminRemoveMembership_request({
        userId: target.userId,
        agencyId: target.agencyId,
        email: lookup?.email,
      }),
    )
    setRemoveTarget(null)
  }

  // The success event flips the card's tag; the endpoints resolve the email to
  // the same entity the lookup showed.
  const confirmStatus = ({ email: target, reason, clickupRef }: TAccountStatusChange) => {
    dispatch(
      statusTarget?.enable
        ? userApiEvents.enableUser_request({ email: target, reason, clickupRef })
        : userApiEvents.disableUser_request({ email: target, reason, clickupRef }),
    )
    setStatusTarget(null)
  }

  return (
    <div className={styles.root}>
      <div className={styles.intro}>
        <h3>Look up a user</h3>
        <p>Resolves the email to the account login actually reaches, and lists every agency it can switch into.</p>
      </div>

      <form className={styles.lookup} onSubmit={onSubmit}>
        <div className={styles.email}>
          <TextField
            inputComponent={
              <TextInput
                type={TextFieldTypes.text}
                name="lookupEmail"
                autoComplete="off"
                placeholder="Email address, or part of one"
                value={email}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => onEmailChange(event.target.value)}
              />
            }
          />
          {suggestions.length > 0 && (
            <ul className={styles.suggestions} role="listbox" aria-label="Matching email addresses">
              {suggestions.map(match => (
                <li key={`${match.email}-${match.userId}`}>
                  <button type="button" className={styles.suggestion} onClick={() => pickMatch(match.email)}>
                    <span>{match.email}</span>
                    {!match.enabled && <span className={cx(styles.tag, styles['tag-bad'])}>Disabled</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button type="submit" size="sm" loading={isLookingUp} disabled={!email.trim()}>
          Look up
        </Button>
      </form>

      {lookup && (
        <>
          <div className={styles.card}>
            <div className={styles.identity}>
              <span className={styles['identity-email']}>{lookup.email}</span>
              <span className={styles.id}>{lookup.userId}</span>
            </div>
            <span className={cx(styles.tag, lookup.enabled ? styles['tag-ok'] : styles['tag-bad'])}>
              {lookup.enabled ? 'Enabled' : 'Disabled'}
            </span>
            <Button
              size="sm"
              secondary
              loading={isChangingStatus}
              onClick={() => setStatusTarget({ email: lookup.email, enable: !lookup.enabled })}
            >
              {lookup.enabled ? 'Disable account' : 'Enable account'}
            </Button>
            <Button size="sm" onClick={() => onGrantAccess(lookup)}>
              Add to agencies
            </Button>
          </div>

          {grantResults && grantResults.length > 0 && (
            <ul className={styles.outcomes} aria-label="Result of the last add">
              {grantResults.map(outcome => (
                <li key={outcome.agencyId} className={styles.outcome}>
                  <span className={cx(styles.tag, styles[`tag-${outcome.status}`])}>
                    {STATUS_LABEL[outcome.status]}
                  </span>
                  <span>{agencyNames.get(outcome.agencyId) ?? outcome.agencyId}</span>
                  {testAgencyIds.has(outcome.agencyId) && <TestAgencyPill />}
                  {outcome.error && <span className={styles['outcome-error']}>{outcome.error}</span>}
                </li>
              ))}
            </ul>
          )}

          {lookup.agencies.length === 0 ? (
            <NoContent heading="No agencies" subHeading="This account is not a member of any agency." />
          ) : (
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Cell head>Agency</Table.Cell>
                  <Table.Cell head>Role</Table.Cell>
                  <Table.Cell head>{null}</Table.Cell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {lookup.agencies.map(agency => {
                  const agencyLabel =
                    agency.tradingAs || agency.agencyName
                      ? agencyDisplayName(agency)
                      : agencyNames.get(agency.agencyId) ?? agency.agencyId
                  return (
                    <Table.Row key={agency.agencyId}>
                      <Table.Cell>
                        <span className={styles.agency}>
                          <span className={styles['agency-name']}>
                            <span className={styles.strong}>{agencyLabel}</span>
                            {testAgencyIds.has(agency.agencyId) && <TestAgencyPill />}
                          </span>
                          {agencyEntityLine(agency) && (
                            <span className={styles['agency-entity']}>{agencyEntityLine(agency)}</span>
                          )}
                          <span className={styles.id}>{agency.agencyId}</span>
                        </span>
                      </Table.Cell>
                      <Table.Cell>{roleLabel(agency.role)}</Table.Cell>
                      <Table.Cell className={styles.actions}>
                        <Button
                          size="xs"
                          secondary
                          onClick={() =>
                            setRemoveTarget({
                              userId: lookup.userId,
                              agencyId: agency.agencyId,
                              userLabel: lookup.email,
                              agencyLabel,
                            })
                          }
                        >
                          Remove
                        </Button>
                      </Table.Cell>
                    </Table.Row>
                  )
                })}
              </Table.Body>
            </Table>
          )}
        </>
      )}

      <RemoveMembershipModal target={removeTarget} onCancel={() => setRemoveTarget(null)} onConfirm={confirmRemove} />
      <AccountStatusModal target={statusTarget} onCancel={() => setStatusTarget(null)} onConfirm={confirmStatus} />
    </div>
  )
}

export default UserAccess
